import {
  type DrawingStyle,
  makeDrawingStyle,
} from '@features/drawing/model/reducers/drawingSettingsReducer.js';
import { createReducer } from '@reduxjs/toolkit';
import { toggleColorizeLegend } from '@shared/colorizers/colorizeSettings.js';
import {
  type ColorizingMode,
  colorizerNeedsElevation,
} from '@shared/colorizers/index.js';
import { elevationCoverage } from '@shared/geoutils.js';
import type { LabelVisibility } from '@shared/labelVisibility.js';
import type { TraceOutput } from '../traceServices.js';
import { isRecording } from '../trackChannels.js';
import { isTrackLine } from '../trackSelection.js';
import {
  dataViewerColorizeTrackBy,
  dataViewerSetColorizeLegend,
  dataViewerSetData,
  dataViewerSetLabelVisibility,
  dataViewerSetStyle,
  dataViewerSetTraceOutput,
} from './actions.js';

// Persisted track-viewer display preferences. A dedicated settings slice (not
// the transient track data), so they survive map clears without per-handler
// preservation.
export interface DataViewerSettingsState {
  // Style applied to imported track-viewer features that carry no style of
  // their own.
  style: DrawingStyle;
  labelVisibility: LabelVisibility;
  colorizeTrackBy: ColorizingMode | null;
  // Whether the colorize legend is shown; independent of the other tools.
  colorizeLegend: boolean;
  // The trace modal's last result; unset, it is chosen by the device.
  traceOutput: TraceOutput | null;
}

export const dataViewerSettingsInitialState: DataViewerSettingsState = {
  style: makeDrawingStyle('#0000ff', 4),
  labelVisibility: 'always',
  colorizeTrackBy: null,
  colorizeLegend: true,
  traceOutput: null,
};

export const dataViewerSettingsReducer = createReducer(
  dataViewerSettingsInitialState,
  (builder) =>
    builder
      .addCase(dataViewerSetStyle, (state, action) => {
        state.style = action.payload;
      })
      .addCase(dataViewerSetLabelVisibility, (state, action) => {
        state.labelVisibility = action.payload;
      })
      .addCase(dataViewerColorizeTrackBy, (state, action) => {
        state.colorizeTrackBy = action.payload;
      })
      .addCase(dataViewerSetColorizeLegend, (state, { payload }) => {
        toggleColorizeLegend(state, payload);
      })
      .addCase(dataViewerSetTraceOutput, (state, action) => {
        state.traceOutput = action.payload;
      })
      .addCase(dataViewerSetData, (state, action) => {
        // A persisted elevation-derived colorize mode would render as a flat
        // mid-palette on a recording that lacks full elevation (other lines are
        // drawn from the terrain); drop it so the new track starts uncolorized
        // rather than in a misleading state. Cleared in the same dispatch as the
        // track data, so the URL/history sees one atomic change.
        const recordings = (action.payload.trackGeojson?.features ?? [])
          .filter(isTrackLine)
          .filter(isRecording);

        if (
          state.colorizeTrackBy &&
          colorizerNeedsElevation(state.colorizeTrackBy) &&
          recordings.length > 0 &&
          elevationCoverage(recordings) !== 'full'
        ) {
          state.colorizeTrackBy = null;
        }
      }),
);
